import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ApiClient } from '@london-life/api-client';
import { ApiRequestError } from '@london-life/api-client';
import { AccountHold, type PlayerProfile, type UserSummary } from '@london-life/contracts';
import { describeError, type FriendlyError } from '@london-life/errors';
import type { BrowserAuthProvider } from './provider';

export type SessionState =
  | { status: 'loading' }
  | { status: 'guest' }
  | { status: 'signed-in'; user: UserSummary; profile: PlayerProfile; isAdmin: boolean }
  // the account is suspended or banned: the player is told why (and until when)
  | { status: 'held'; hold: AccountHold }
  // the session could not be checked (offline, server down): say so instead of pretending to be signed out
  | { status: 'unavailable'; error: FriendlyError };
type Audience = 'player' | 'admin';

// the one sign-in flow for both apps: provider token in, API session cookie out
export function useSession(
  api: ApiClient,
  provider: BrowserAuthProvider,
  audience: Audience,
  csrf: { get: () => string | null; set: (token: string | null) => void },
) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      csrf.set(me.csrfToken);
      setState({ status: 'signed-in', user: me.user, profile: me.profile, isAdmin: me.isAdmin });
    } catch (error) {
      if (error instanceof ApiRequestError && error.status === 401) setState({ status: 'guest' });
      else if (error instanceof ApiRequestError && error.body?.code === 'account_held') {
        const hold = AccountHold.safeParse((error.body.details as { hold?: unknown } | undefined)?.hold);
        setState({ status: 'held', hold: hold.success ? hold.data : { kind: 'banned', until: null, reason: null } });
      } else throw error;
    }
  }, [api, csrf]);
  // check the session; a failure other than "not signed in" is shown with Try again
  const check = useCallback(() => {
    setState({ status: 'loading' });
    void refresh().catch((error: unknown) => setState({ status: 'unavailable', error: describeError(error) }));
  }, [refresh]);
  useEffect(check, [check]);

  const finish = useCallback(
    async (idToken: string) => {
      const session = await api.createSession({ idToken, audience });
      csrf.set(session.csrfToken);
      await refresh();
    },
    [api, audience, csrf, refresh],
  );
  const actions = useMemo(
    () => ({
      signInWithGoogle: async () => finish(await provider.signInWithGoogle()),
      signInWithEmail: async (email: string, password: string) =>
        finish(await provider.signInWithEmail(email, password)),
      signUpWithEmail: async (email: string, password: string, displayName: string) =>
        finish(await provider.signUpWithEmail(email, password, displayName)),
      sendPasswordReset: (email: string) => provider.sendPasswordReset(email),
      resendVerification: () => provider.sendVerificationEmail(),
      // the player says they clicked the link: if so, a new session carries the verified email
      confirmEmailVerified: async () => {
        const token = await provider.checkEmailVerified();
        if (!token) return false;
        await finish(token);
        return true;
      },
      signOut: async () => {
        await api.signOut().catch(() => undefined);
        await provider.signOut();
        csrf.set(null);
        setState({ status: 'guest' });
      },
      refresh,
      check,
    }),
    [api, check, csrf, finish, provider, refresh],
  );
  return { state, ...actions };
}

// the CSRF token is kept in memory and sessionStorage, never in a readable cookie
export function createCsrfStore(key = 'll_csrf') {
  let token: string | null = null;
  try {
    token = sessionStorage.getItem(key);
  } catch {
    token = null;
  }
  return {
    get: () => token,
    set: (value: string | null) => {
      token = value;
      try {
        if (value) sessionStorage.setItem(key, value);
        else sessionStorage.removeItem(key);
      } catch {
        /* storage unavailable */
      }
    },
  };
}
